import { MutableRefObject, useCallback, useRef } from "react";

import { CampaignWriteOrigin } from "./useCampaignAutosave.types";

/** A flush joins the pending autosave before deciding whether another write is necessary. */
export function useCampaignSaveQueue(args: {
	save: (origin: CampaignWriteOrigin) => Promise<void>;
	snapshot: () => string;
	identity: () => string;
	lastSavedRef: MutableRefObject<string | null>;
	isPausedRef: MutableRefObject<boolean>;
	markSettled: () => void;
}) {
	const latestRef = useRef(args);

	latestRef.current = args;
	const pendingRef = useRef<Promise<void> | null>(null);

	return useCallback(async (origin: CampaignWriteOrigin) => {
		const identity = latestRef.current.identity();

		while (pendingRef.current) {
			await pendingRef.current;
			const current = latestRef.current;

			if (
				identity !== current.identity() ||
				current.lastSavedRef.current === null ||
				current.isPausedRef.current
			) {
				return;
			}
		}

		const current = latestRef.current;
		const state = current.snapshot();

		if (state === current.lastSavedRef.current) {
			current.markSettled();

			return;
		}

		current.lastSavedRef.current = state;
		const pending = current.save(origin);

		pendingRef.current = pending;
		try {
			await pending;
		} finally {
			if (pendingRef.current === pending) {
				pendingRef.current = null;
			}
		}
	}, []);
}
